import BuyTravel from './BuyTravelComponent';
import { defaultConfig } from '@/cms/api/ApiConfig';
import store from '@/store/store';
import AxiosService from '@/views/calculators/services/axiosService';
import {
	callAllCalculations,
	getRisksForPackage,
	mapCoveragesFromProductConfig,
	onePackageCalcEpi,
	prepareCalculations,
	setUpExcesses,
	setupCalculator,
	setupPackages,
	updateCalcKeys,
} from '../services/CalculationService';
import { formatNumber } from '../services/ValidationService';
import { TravelSettings } from './TravelSettings';
import { TravelModel } from './TravelModel';
import { exception } from '@/appinsights/logging/ApplicationInsightLog';
import { PlusCustomerType } from '../services/ProductService';
import { Calculator } from '../BuyInsuranceComponent';

export default class TravelCalculator implements Calculator {
	public model: TravelModel; // the data structure for state and properties
	public cms: TravelSettings; // settings from EPI
	public calcConfig: any;
	public successCalc = true;
	public axiosService: AxiosService;
	private readonly calculatorUrl: string;
	public calculatorInfo: Array<any>;

	constructor(buyTravel: BuyTravel) {
		this.model = buyTravel.model;
		this.cms = buyTravel.cms;
		this.calculatorInfo = JSON.parse(buyTravel.calculatorInfoBlock.markup);

		this.calculatorUrl = defaultConfig.baseUrl + store.state.openServiceCalculatorUrl + '/insurance/travel/quotes';
		this.axiosService = new AxiosService(this.calculatorUrl);

		this.setUpConfig();
	}

	public setUpConfig() {
		setupCalculator(this, this.calculatorInfo);

		this.model.calculation.abCalc.segmentedProductProperties = this.calcConfig.segmentedProductProperties;
		this.model.calculation.abCalc.familyMemberCountOptions = this.calcConfig.familyMemberCountOptions;

		setupPackages(this.model, this.cms, this.calcConfig);

		this.mapCoveragesFromCalc();
		updateCalcKeys(this.model);
	}

	public setupExcessList() {
		setUpExcesses(this.model);
	}

	private mapCoveragesFromCalc() {
		mapCoveragesFromProductConfig(this.model, this.cms);
		// change sum on afbestilling if other sum present in package
		this.model.calculation.abCalc.packages.forEach((pack, packageInx) => {
			try {
				if (pack.sums) {
					// override default CMS settings if present in productConfig
					if (pack.sums[0]?.sum && pack.sums[0]?.covId) {
						this.cms.cancelSum = pack.sums[0].sum;
						this.cms.cancelSumId = pack.sums[0].covId;
					}
				}

				// only default excess
				const res = this.model.calculation.allCoverages.values().next().value.get(packageInx);
				res.forEach((cov) => {
					if (cov.featureId === this.cms.cancelSumId) {
						// '3' - afbestilling
						const sumFormatted = formatNumber.format(this.cms.cancelSum);
						cov.description = cov.description
							.replace('30.000', sumFormatted)
							.replace('60.000', sumFormatted)
							.replace('100.000', sumFormatted)
							.replace('150.000', sumFormatted);
					}
				});
			} catch (e) {
				exception(e);
				// wrong config
			}
		});
	}

	public async getCalculations(): Promise<boolean> {
		prepareCalculations(this.model, this.cms, true);
		const res = await callAllCalculations(
			this,
			this.model.calculation.abCalc.excessIdDefault,
			false,
			'Travel calculator'
		);
		this.model.choosePackage.ownRiskId = this.model.calculation.abCalc.excessIdDefault;
		return res;
	}
	public async onePackageCalcEpi(monthly: boolean, excessId: number, packageId: number) {
		return await onePackageCalcEpi(this, monthly, excessId, packageId, true, 'Travel calculator');
	}

	public createParamsEpi(excessId: number, packageId: number, monthlyPrices: number) {
		const chosenAge =
			this.model.personInfo.isOldestInHousehold === 'ja'
				? this.model.personInfo.customerAge
				: this.model.personInfo.oldestInHouseholdAge;

		const customerAge = parseInt(chosenAge.trim().replace(/\D/g, ''));

		let cancelSum = this.cms.cancelSum; // afbestillingssum - default from CMS

		// change sum on afbestilling if sum present in package
		this.model.calculation.abCalc.packages.forEach((pack) => {
			if (pack.id === packageId) {
				if (pack.sums) {
					try {
						cancelSum = pack.sums[0].sum;
					} catch (e) {
						exception(e);
						// wrong config
					}
				}
			}
		});

		let groupAgreement = 0;
		if (this.model.campaign.valid) {
			groupAgreement = this.cms.campaigns.get(this.model.campaign.ID).groupId;
		}

		// create params
		const params = {
			plus_customer: PlusCustomerType.PK0.toUpperCase(),
			customer_age: customerAge,
			family_member_count: this.model.personInfo.familyMemberCountId,
			destination: this.model.choosePackage.segmentedProductProperty,
			post_code: `${this.model.personInfo.zipCode}`,
			group_agreement: groupAgreement < 1 ? groupAgreement : undefined,
			cancel_sum: cancelSum,
			payments_per_year: monthlyPrices === 1 ? 12 : 1,
			selected: getRisksForPackage(packageId, this.model),
		};
		return params;
	}
}
